/**
 * Grace Period Banner
 * Displays a persistent banner when an organization has a pending plan change with grace period.
 * Shown to paid roles and owners only.
 */
import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import api from '../../services/api';

const GracePeriodBanner = ({ organizationId, userRole, onVisibilityChange }) => {
  const [graceInfo, setGraceInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkGracePeriod = async () => {
      if (!organizationId) {
        setLoading(false);
        onVisibilityChange?.(false);
        return;
      }

      try {
        const res = await api.get(`/api/admin/org-plans/grace-period/${organizationId}`);
        if (res.data.has_grace_period) {
          setGraceInfo(res.data);
          onVisibilityChange?.(true);
        } else {
          onVisibilityChange?.(false);
        }
      } catch (err) {
        // Silently fail - banner just won't show
        console.debug('Grace period check failed:', err);
        onVisibilityChange?.(false);
      } finally {
        setLoading(false);
      }
    };

    checkGracePeriod();
  }, [organizationId, onVisibilityChange]);

  // Don't show for non-paid roles
  const isPaidRole = ['owner', 'product_manager', 'billing_admin', 'admin'].includes(userRole);
  
  const isVisible = !loading && !dismissed && graceInfo && isPaidRole;
  
  useEffect(() => {
    onVisibilityChange?.(isVisible);
  }, [isVisible, onVisibilityChange]);
  
  if (!isVisible) {
    return null;
  }

  const daysRemaining = graceInfo.days_remaining || 0;
  const isUrgent = daysRemaining <= 3;

  return (
    <div className={cn(
      "fixed top-0 left-0 right-0 z-50 px-4 py-3 flex items-center justify-between",
      isUrgent ? "bg-red-600 text-white" : "bg-amber-500 text-white"
    )}>
      <div className="flex items-center gap-3 flex-wrap">
        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">
            Plan Change Pending
          </span>
          <span className="hidden sm:inline">·</span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
          </span>
          <span className="hidden md:inline">·</span>
          <span className="text-sm opacity-90 hidden md:inline">
            Resolve conflicts or changes will be applied automatically.
          </span>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="bg-white/20 border-white/30 text-white hover:bg-white/30"
          onClick={() => window.location.href = '/team'}
        >
          Resolve Now
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="text-white/80 hover:text-white hover:bg-white/20"
          onClick={() => setDismissed(true)}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default GracePeriodBanner;
